import { useDataTypesHooks, useDescribeHooks } from "../../../features/data-types"
import { FieldType, useFiltersHooks } from "../../../features/ui"
import { DataType, TableName } from "../../../tables"
import { isDefined } from "../../../utils/typeUtils"
import { useLoaders } from "../../common"
import { useSaleOperationLoaders } from "../../sales"
import { TransactionType } from "../components/transaction/transaction-list/types"
import { usePaymentOrderLoaders } from "../loaders/paymentOrderLoaders"
import { useTransactionLoaders } from "../loaders/transactionLoaders"
import { Transaction, TransactionDestinationType, TransactionSourceType } from "../state/types"

export const useTransactionHooks = () => {
    const { isOutcome } = transactionHelpers
    const { describe } = useDescribeHooks()
    const { search2 } = useFiltersHooks()
    const { formatValue } = useDataTypesHooks()
    
    const loaders = useLoaders()
    const transactionLoaders = useTransactionLoaders()
    const saleOperationLoaders = useSaleOperationLoaders()
    const paymentOrderLoaders = usePaymentOrderLoaders()

    const describeTransaction = async (transaction: Transaction) =>
        `${await describeDestination(transaction)} (${formatValue(transaction.amount, FieldType.PRICE)})`

    const describeSource = async (transaction: Transaction) => {
        if (transaction.sourceType === TransactionSourceType.MANUAL_ADDITION) {
            return 'Adición Manual'
        } else if (transaction.sourceType === TransactionSourceType.CANCELLATION) {
            return 'Cancelación'
        } else {
            const table = getSourceTable(transaction.sourceType)!
            const source = await getSource(transaction.sourceType, transaction.sourceId)
            return describe(table, source!)
        }
    }

    const describeDestination = async (transaction: Transaction) => {
        if (transaction.destinationType === TransactionDestinationType.MANUAL_SUBSTRACTION) {
            return 'Retiro Manual'
        } else if (transaction.destinationType === TransactionDestinationType.FINANCIAL_COST) {
            return 'Costo Financiero'
        } else if (transaction.destinationType === TransactionDestinationType.ROUNDING) {
            return 'Redondeo'
        } else if (transaction.destinationType === TransactionDestinationType.CREDIT_IN_FAVOR) {
            return 'Crédito a Favor'
        } else {
            const table = getDestinationTable(transaction.destinationType)!
            const destination = await getDestination(transaction.destinationType, transaction.destinationId)
            return describe(table, destination!)
        }
    }

    const getSourceTable = (sourceType: TransactionSourceType) => {
        return new Map<TransactionSourceType, TableName>()
            .set(TransactionSourceType.CASH_FUND, TableName.CASH_FUNDS)
            .set(TransactionSourceType.CHECK, TableName.CHECKS)
            .set(TransactionSourceType.CREDIT_CARD, TableName.CREDIT_CARDS)
            .set(TransactionSourceType.CREDIT_PAYMENT, TableName.CREDIT_PAYMENTS)
            .set(TransactionSourceType.SALE, TableName.SALE_ORDERS)
            .set(TransactionSourceType.HOARD, TableName.HOARD_ORDERS)
            .get(sourceType)
    }

    const getSource = async (
        sourceType: TransactionSourceType,
        sourceId?: string
    ) => {
        const { load } = loaders()
        const table = getSourceTable(sourceType)
        return table && sourceId ? load(table, sourceId) : undefined
    }

    const getDestinationTable = (destinationType: TransactionDestinationType) => {
        return new Map<TransactionDestinationType, TableName>()
            .set(TransactionDestinationType.CASH_FUND, TableName.CASH_FUNDS)
            .set(TransactionDestinationType.CHECK, TableName.CHECKS)
            .set(TransactionDestinationType.PAYMENT_ORDER, TableName.PAYMENT_ORDERS)
            .get(destinationType)
    }

    const getDestination = async (
        destinationType: TransactionDestinationType,
        destinationId?: string
    ) => {
        const { load } = loaders()
        const table = getDestinationTable(destinationType)
        return table && destinationId ? load(table, destinationId) : undefined
    }

    const searchTransactions = async (
        searchText = '',
        dateFrom?: Date,
        dateTo?: Date,
        cashFundId?: string,
        transactionType?: TransactionType,
        amountFrom?: number,
        amountTo?: number
    ) => {
        const { loadTransactions } = transactionLoaders()
        const defaultDateFrom = new Date()
        defaultDateFrom.setFullYear(defaultDateFrom.getFullYear() - 1)
        const defaultDateTo = new Date()
        defaultDateTo.setFullYear(defaultDateTo.getFullYear() + 1)
        const transactions = await loadTransactions(dateFrom || defaultDateFrom, dateTo || defaultDateTo)
        
        const { loadSaleOperations } = saleOperationLoaders()
        const sourceIds = transactions.filter(transaction => !!transaction.sourceId).map(transaction => transaction.sourceId!)
        const operations = await loadSaleOperations(sourceIds)
        const { loadPaymentOrders } = paymentOrderLoaders()
        const destinationIds = transactions.filter(transaction => !!transaction.destinationId).map(transaction => transaction.destinationId!)
        const paymentOrders = await loadPaymentOrders(destinationIds)
        const valuesFn = async (transactionDT: DataType) => {
            const transaction = transactionDT as Transaction
            const operation = operations.find(operation => operation.id === transaction.sourceId || operation.id === transaction.destinationId)
            const paymentOrder = paymentOrders.find(paymentOrder => paymentOrder.id === transaction.destinationId)
            const values = []
            operation && values.push(operation.code.toString())
            paymentOrder && values.push(paymentOrder.code.toString())
            return values
        }

        const signedAmount = (transaction: Transaction, cashFundIdParam?: string) => isOutcome(transaction, cashFundIdParam) ? -transaction.amount : transaction.amount
        const filterFn = (transactionDT: DataType) => {
            const transaction = transactionDT as Transaction
            return (!isDefined(amountFrom) || signedAmount(transaction, cashFundId) >= amountFrom!) &&
                (!isDefined(amountTo) || signedAmount(transaction, cashFundId) <= amountTo!) &&
                (!cashFundId || transaction.sourceId === cashFundId || transaction.destinationId === cashFundId) &&
                (!transactionType || (
                    transactionType === TransactionType.ALL ||
                    transactionType === TransactionType.INCOME && !isOutcome(transaction, cashFundId) ||
                    transactionType === TransactionType.OUTCOME && isOutcome(transaction, cashFundId)
                ))
        }

        const sortFn = (transactionDT_A: DataType, transactionDT_B: DataType) =>
            (transactionDT_B as Transaction).date.localeCompare((transactionDT_A as Transaction).date)

        return search2(transactions, searchText, valuesFn, filterFn, sortFn) as Promise<Transaction[]>
    }

    return {
        describeTransaction,
        describeSource,
        describeDestination,
        getSourceTable,
        getSource,
        getDestinationTable,
        getDestination,
        searchTransactions
    }
}

export const transactionHelpers = {
    isOutcome: (transaction: Transaction, cashFundId?: string): boolean => {
        const { sourceType, sourceId, destinationType } = transaction
        const sourceIsCashFund = sourceType === TransactionSourceType.CASH_FUND
        const destinationIsCashFund = destinationType === TransactionDestinationType.CASH_FUND
        return cashFundId ? sourceId === cashFundId : sourceIsCashFund && !destinationIsCashFund
    }
}
